import { Link } from "react-router-dom";
import {
  CheckCircle,
  Droplets,
  Thermometer,
  TriangleAlert,
  Toilet,
  Phone,
  ArrowRight,
} from "lucide-react";

const SERVICE_CARDS = [
  {
    icon: TriangleAlert,
    title: "Burst Pipe Repair",
    desc: "We fix burst pipes fast to prevent further water damage to your home.",
  },
  {
    icon: Droplets,
    title: "Emergency Leak Fix",
    desc: "Fast response to stop leaks and limit damage before it spreads.",
  },
  {
    icon: Toilet,
    title: "Toilet Repair",
    desc: "Fast toilet repairs done right the first time, every time.",
  },
  {
    icon: Thermometer,
    title: "Water Heater Repair",
    desc: "Restore reliable hot water with same-day water heater service.",
  },
];

const CHECKLIST = [
  "Clear diagnosis and repair options explained before work begins.",
  "Professional tools and quality parts used on every job.",
  "Clean, careful work with full respect for your property.",
  "Upfront pricing — no surprises, ever.",
];

export default function Services() {
  return (
    <section id="services" className="max-w-[90%] xl:max-w-6xl mx-auto">

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

        {/* Left: heading, checklist, CTAs */}
        <div className="space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full">
              What We Do
            </div>
            <h2 className="font-black text-gray-900 text-4xl lg:text-5xl tracking-tight leading-tight">
              Services We Offer
            </h2>
            <p className="text-gray-500 text-lg leading-relaxed max-w-md">
              Whether it's urgent or routine, we focus on fast response, clear pricing, and getting the job done right the first time.
            </p>
          </div>

          <ul className="space-y-4">
            {CHECKLIST.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-600 text-[15px] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/services"
              className="flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-gray-700 font-semibold px-6 py-3.5 rounded-xl text-sm"
            >
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:(312) 555-0147"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 transition-colors text-white font-bold px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/25 text-sm"
            >
              <Phone className="w-4 h-4" /> Call for Service
            </a>
          </div>
        </div>

        {/* Right: service cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SERVICE_CARDS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="group bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col items-start gap-4"
            >
              <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                <Icon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="space-y-1.5">
                <h4 className="font-bold text-gray-900 text-[15px]">{title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
              <a
                href="tel:(312) 555-0147"
                className="mt-auto text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
              >
                Call Now <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}